import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { calendarAPI, casesAPI, tasksAPI, teamAPI } from '../lib/api';
import { formatDate, formatDateTime } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Calendar as CalendarIcon, 
  Plus, 
  Clock,
  RefreshCcw,
  Link2,
  Unlink,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Briefcase,
  Users,
  ChevronLeft,
  ChevronRight,
  Filter,
  Search,
  Gavel,
  FileText,
  Bell,
  AlertTriangle,
  Timer,
  Eye,
  MoreVertical,
  Trash2,
  Edit,
  MapPin,
  User,
  X,
  ChevronDown,
  ListTodo,
  LayoutGrid,
  List,
  Target,
  Play,
  Square,
  DollarSign,
  Building2,
  TrendingUp,
  Scale,
  History,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { PageLoader } from '../components/common/LoadingSpinner';
import { FeatureGate } from '../components/common/FeatureGate';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../components/ui/sheet';
// Advanced Calendar Components
import { CaseTimeline } from '../components/calendar/CaseTimeline';
import { CaseProgressFunnel, MasterCaseFunnel } from '../components/calendar/CaseProgressFunnel';
import { JurisdictionDeadlines } from '../components/calendar/JurisdictionDeadlines';
import { CaseCalendarTemplates } from '../components/calendar/CaseCalendarTemplates';
import { SmartReminders } from '../components/calendar/SmartReminders';
import { ConflictDetection } from '../components/calendar/ConflictDetection';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { cn } from '../lib/utils';

// Legal Event Types with their configurations
const EVENT_TYPES = {
  court_appearance: { 
    label: 'Court Appearance', 
    icon: Gavel, 
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    priority: 'critical',
    nonMovable: true
  },
  filing_deadline: { 
    label: 'Filing Deadline', 
    icon: FileText, 
    color: 'bg-red-100 text-red-800 border-red-200',
    priority: 'critical',
    nonMovable: true
  },
  limitation_deadline: { 
    label: 'Limitation Deadline', 
    icon: AlertTriangle, 
    color: 'bg-red-200 text-red-900 border-red-300',
    priority: 'critical',
    nonMovable: true
  },
  hearing: { 
    label: 'Hearing', 
    icon: Gavel, 
    color: 'bg-violet-100 text-violet-800 border-violet-200',
    priority: 'high',
    nonMovable: true
  },
  motion: { 
    label: 'Motion', 
    icon: FileText, 
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    priority: 'high',
    nonMovable: false
  },
  client_meeting: { 
    label: 'Client Meeting', 
    icon: Users, 
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    priority: 'medium',
    nonMovable: false
  },
  internal_task: { 
    label: 'Internal Task', 
    icon: ListTodo, 
    color: 'bg-slate-100 text-slate-800 border-slate-200',
    priority: 'low',
    nonMovable: false
  },
  follow_up: { 
    label: 'Follow-Up', 
    icon: Bell, 
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    priority: 'medium',
    nonMovable: false
  },
  meeting: { 
    label: 'Meeting', 
    icon: Users, 
    color: 'bg-green-100 text-green-800 border-green-200',
    priority: 'medium',
    nonMovable: false
  },
  reminder: { 
    label: 'Reminder', 
    icon: Bell, 
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    priority: 'low',
    nonMovable: false
  },
  deadline: { 
    label: 'Deadline', 
    icon: AlertCircle, 
    color: 'bg-red-100 text-red-800 border-red-200',
    priority: 'high',
    nonMovable: true
  },
  google: { 
    label: 'Google Calendar', 
    icon: CalendarIcon, 
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    priority: 'low',
    nonMovable: false
  },
};

const PRIORITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };

const VIEW_MODES = {
  firm: { label: 'Firm View', icon: Building2, description: 'All firm events' },
  user: { label: 'My Calendar', icon: User, description: 'Your assigned events' },
  case: { label: 'Case View', icon: Briefcase, description: 'Single case timeline' },
  deadlines: { label: 'Deadlines', icon: AlertTriangle, description: 'Court & filing deadlines' },
  agenda: { label: 'Agenda', icon: List, description: 'List view' },
  timeline: { label: 'Case Timeline', icon: History, description: 'Visual case history' },
  funnel: { label: 'Pipeline', icon: TrendingUp, description: 'Master case funnel' },
  rules: { label: 'Court Rules', icon: Scale, description: 'Deadline intelligence' },
  templates: { label: 'Templates', icon: FileText, description: 'Auto-generate calendars' },
  reminders: { label: 'Smart Reminders', icon: Bell, description: 'Intelligent reminders' },
  conflicts: { label: 'Conflict Check', icon: AlertCircle, description: 'Detect scheduling issues' },
};

export const CalendarPage = () => {
  const { hasFeature, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Data states
  const [events, setEvents] = useState([]);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [cases, setCases] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDrawerOpen, setEventDrawerOpen] = useState(false);
  const [activeTimer, setActiveTimer] = useState(null);
  const [timerLoading, setTimerLoading] = useState(false);
  
  // View & Filter states
  const [viewMode, setViewMode] = useState('user');
  const [selectedCase, setSelectedCase] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showTasksOnCalendar, setShowTasksOnCalendar] = useState(true);
  const [filterPriority, setFilterPriority] = useState('all');
  
  // Google status
  const [googleStatus, setGoogleStatus] = useState({ connected: false, email: null });
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    case_id: '',
    client_id: '',
    event_type: 'meeting',
    priority: 'medium',
    location: '',
    owner_id: '',
    participants: [],
    reminders: [{ time: 24, unit: 'hours' }],
    non_movable: false,
    all_day: false,
  });

  useEffect(() => {
    const initCalendar = async () => {
      if (hasFeature('calendar')) {
        await fetchAllData();
        await checkGoogleStatus();
        
        const connected = searchParams.get('connected');
        if (connected === 'google') {
          toast.success('Google Calendar connected successfully!');
        }
        
        const error = searchParams.get('error');
        if (error) {
          toast.error(`Connection failed: ${error}`);
        }
      } else {
        setLoading(false);
      }
    };
    initCalendar();
  }, [hasFeature, searchParams]);

  const fetchAllData = async () => {
    try {
      const [eventsRes, casesRes, tasksRes, teamRes] = await Promise.all([
        calendarAPI.getEvents(),
        casesAPI.getAll(),
        tasksAPI.getAll().catch(() => ({ data: [] })),
        teamAPI.getAll().catch(() => ({ data: [] })),
      ]);
      setEvents(eventsRes.data || []);
      setCases(casesRes.data || []);
      setTasks(tasksRes.data || []);
      setTeamMembers(teamRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkGoogleStatus = async () => {
    try {
      const response = await calendarAPI.getGoogleStatus();
      setGoogleStatus(response.data);
      if (response.data.connected) {
        fetchGoogleEvents();
      }
    } catch (error) {
      console.error('Failed to check Google status:', error);
    }
  };

  const fetchGoogleEvents = async () => {
    try {
      const response = await calendarAPI.getGoogleEvents();
      setGoogleEvents(response.data || []);
    } catch (error) {
      console.error('Failed to fetch Google events:', error);
    }
  };

  const handleConnectGoogle = async () => {
    try {
      const response = await calendarAPI.connectGoogle(window.location.origin);
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      toast.error('Failed to initiate Google Calendar connection');
    }
  };

  const handleDisconnectGoogle = async () => {
    try {
      await calendarAPI.disconnectGoogle();
      setGoogleStatus({ connected: false, email: null });
      setGoogleEvents([]);
      toast.success('Google Calendar disconnected');
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const handleSyncCalendar = async () => {
    setSyncing(true);
    try {
      const response = await calendarAPI.syncCalendar();
      toast.success(`Synced! Imported ${response.data.imported}, Exported ${response.data.exported} events`);
      fetchAllData();
      fetchGoogleEvents();
    } catch (error) {
      toast.error('Sync failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSyncing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        start_time: formData.all_day 
          ? new Date(formData.start_time).toISOString().split('T')[0] + 'T00:00:00Z'
          : new Date(formData.start_time).toISOString(),
        end_time: formData.all_day 
          ? new Date(formData.end_time || formData.start_time).toISOString().split('T')[0] + 'T23:59:59Z'
          : new Date(formData.end_time).toISOString(),
      };
      
      if (!data.case_id || data.case_id === 'none') delete data.case_id;
      if (!data.client_id) delete data.client_id;
      if (!data.owner_id) data.owner_id = user?.user_id;
      
      if (editingEvent) {
        await calendarAPI.updateEvent(editingEvent.event_id, data);
        toast.success('Event updated');
      } else {
        await calendarAPI.createEvent(data);
        toast.success('Event created');
      }
      
      fetchAllData();
      resetForm();
    } catch (error) {
      toast.error(editingEvent ? 'Failed to update event' : 'Failed to create event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!confirm('Are you sure you want to delete this event?')) return;
    try {
      await calendarAPI.deleteEvent(eventId);
      toast.success('Event deleted');
      setEventDrawerOpen(false);
      setSelectedEvent(null);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      start_time: '',
      end_time: '',
      case_id: '',
      client_id: '',
      event_type: 'meeting',
      priority: 'medium',
      location: '',
      owner_id: '',
      participants: [],
      reminders: [{ time: 24, unit: 'hours' }],
      non_movable: false,
      all_day: false,
    });
    setIsDialogOpen(false);
    setEditingEvent(null);
  };

  const openEditEvent = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title || '',
      description: event.description || '',
      start_time: event.start_time ? new Date(event.start_time).toISOString().slice(0, 16) : '',
      end_time: event.end_time ? new Date(event.end_time).toISOString().slice(0, 16) : '',
      case_id: event.case_id || '',
      client_id: event.client_id || '',
      event_type: event.event_type || 'meeting',
      priority: event.priority || 'medium',
      location: event.location || '',
      owner_id: event.owner_id || '',
      participants: event.participants || [],
      reminders: event.reminders || [{ time: 24, unit: 'hours' }],
      non_movable: event.non_movable || false,
      all_day: event.all_day || false,
    });
    setIsDialogOpen(true);
    setEventDrawerOpen(false);
  };

  const openEventDrawer = (event) => {
    setSelectedEvent(event);
    setEventDrawerOpen(true);
  };

  // Get linked case info
  const getCaseInfo = (caseId) => cases.find(c => c.case_id === caseId);

  // Convert tasks to calendar events
  const taskEvents = useMemo(() => {
    if (!showTasksOnCalendar) return [];
    return tasks
      .filter(t => t.due_date && t.status !== 'completed')
      .map(task => ({
        event_id: `task_${task.task_id}`,
        title: task.title,
        description: task.description,
        start_time: task.due_date,
        end_time: task.due_date,
        event_type: 'internal_task',
        priority: task.priority || 'medium',
        case_id: task.case_id,
        owner_id: task.assignee_id,
        is_task: true,
        task_id: task.task_id,
        task_status: task.status,
      }));
  }, [tasks, showTasksOnCalendar]);

  // Combine all events
  const allEvents = useMemo(() => {
    const combined = [
      ...events.map(e => ({ ...e, source: 'local' })),
      ...googleEvents.map(e => ({ ...e, source: 'google', event_type: 'google' })),
      ...taskEvents.map(e => ({ ...e, source: 'task' })),
    ];
    
    // Apply filters
    let filtered = combined;
    
    // View mode filtering
    if (viewMode === 'user') {
      filtered = filtered.filter(e => 
        e.owner_id === user?.user_id || 
        e.participants?.includes(user?.user_id) ||
        e.source === 'google'
      );
    } else if (viewMode === 'case' && selectedCase) {
      filtered = filtered.filter(e => e.case_id === selectedCase);
    } else if (viewMode === 'deadlines') {
      filtered = filtered.filter(e => 
        ['court_appearance', 'filing_deadline', 'limitation_deadline', 'hearing', 'deadline'].includes(e.event_type)
      );
    }
    
    // Priority filter
    if (filterPriority !== 'all') {
      filtered = filtered.filter(e => e.priority === filterPriority);
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(e => 
        e.title?.toLowerCase().includes(query) ||
        e.description?.toLowerCase().includes(query)
      );
    }
    
    // Sort by priority and time
    return filtered.sort((a, b) => {
      const priorityDiff = (PRIORITY_ORDER[a.priority] || 3) - (PRIORITY_ORDER[b.priority] || 3);
      if (priorityDiff !== 0) return priorityDiff;
      return new Date(a.start_time) - new Date(b.start_time);
    });
  }, [events, googleEvents, taskEvents, viewMode, selectedCase, filterPriority, searchQuery, user]);

  // Get events for a specific date
  const getEventsForDate = (date) => {
    return allEvents.filter(event => {
      const eventDate = new Date(event.start_time);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  // Get dates that have events (for calendar highlights)
  const eventDates = useMemo(() => {
    const dates = {};
    allEvents.forEach(event => {
      const date = new Date(event.start_time).toDateString();
      if (!dates[date]) dates[date] = { count: 0, hasDeadline: false, hasCritical: false };
      dates[date].count++;
      if (['filing_deadline', 'limitation_deadline', 'deadline'].includes(event.event_type)) {
        dates[date].hasDeadline = true;
      }
      if (event.priority === 'critical') {
        dates[date].hasCritical = true;
      }
    });
    return dates;
  }, [allEvents]);

  // Check for conflicts
  const getConflicts = (event) => {
    if (!event.start_time || !event.end_time) return [];
    const start = new Date(event.start_time);
    const end = new Date(event.end_time);
    
    return allEvents.filter(e => {
      if (e.event_id === event.event_id) return false;
      if (e.owner_id !== event.owner_id) return false;
      
      const eStart = new Date(e.start_time);
      const eEnd = new Date(e.end_time);
      
      return (start < eEnd && end > eStart);
    });
  };

  // Today's and upcoming deadlines
  const upcomingDeadlines = useMemo(() => {
    const now = new Date();
    const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    return allEvents
      .filter(e => {
        const eventDate = new Date(e.start_time);
        return (
          ['filing_deadline', 'limitation_deadline', 'court_appearance', 'hearing', 'deadline'].includes(e.event_type) &&
          eventDate >= now &&
          eventDate <= weekFromNow
        );
      })
      .slice(0, 5);
  }, [allEvents]);

  // Overdue tasks
  const overdueTasks = useMemo(() => {
    const now = new Date();
    return tasks.filter(t => {
      if (t.status === 'completed') return false;
      const dueDate = new Date(t.due_date);
      return dueDate < now;
    });
  }, [tasks]);

  const selectedDateEvents = getEventsForDate(selectedDate);

  // Start timer for billable events
  const handleStartTimer = async (event) => {
    setTimerLoading(true);
    try {
      const response = await calendarAPI.startTimer(event.event_id);
      setActiveTimer({
        eventId: event.event_id,
        entryId: response.data.entry_id,
        eventTitle: event.title,
        startTime: response.data.start_time
      });
      toast.success(`Timer started for: ${event.title}`);
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already have an active timer')) {
        toast.error(error.response.data.detail);
      } else {
        // Fallback to navigate to time & billing
        toast.info('Opening Time & Billing...');
        navigate('/time-billing', { 
          state: { 
            startTimer: true, 
            caseId: event.case_id,
            description: event.title 
          } 
        });
      }
    } finally {
      setTimerLoading(false);
    }
  };

  const handleStopTimer = async (event) => {
    if (!activeTimer || activeTimer.eventId !== event.event_id) {
      toast.error('No active timer for this event');
      return;
    }
    
    setTimerLoading(true);
    try {
      const response = await calendarAPI.stopTimer(event.event_id);
      toast.success(`Timer stopped: ${response.data.duration_formatted}`);
      setActiveTimer(null);
    } catch (error) {
      toast.error('Failed to stop timer');
    } finally {
      setTimerLoading(false);
    }
  };

  if (!hasFeature('calendar')) {
    return (
      <FeatureGate feature="calendar">
        <div className="h-[600px]" />
      </FeatureGate>
    );
  }

  if (loading) {
    return <PageLoader text="Loading calendar..." />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
            Calendar
          </h1>
          <p className="text-slate-500 mt-1">Case & Deadline Management System</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {/* View Mode Selector */}
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-[160px]" data-testid="view-mode-select">
              <SelectValue placeholder="View Mode" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(VIEW_MODES).map(([key, mode]) => {
                const Icon = mode.icon;
                return (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4" />
                      {mode.label}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          {/* Case Filter (for Case View) */}
          {viewMode === 'case' && (
            <Select value={selectedCase || ''} onValueChange={setSelectedCase}>
              <SelectTrigger className="w-[200px]">
                <Briefcase className="h-4 w-4 mr-2 text-slate-500" />
                <SelectValue placeholder="Select Case" />
              </SelectTrigger>
              <SelectContent>
                {cases.map(c => (
                  <SelectItem key={c.case_id} value={c.case_id}>
                    {c.title || c.case_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Google Calendar Sync */}
          {googleStatus.connected ? (
            <>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleSyncCalendar}
                disabled={syncing}
                className="gap-2"
                data-testid="sync-calendar-btn"
              >
                <RefreshCcw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync'}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Google Connected
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <div className="px-2 py-1.5 text-sm text-slate-600">
                    {googleStatus.email}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleDisconnectGoogle} className="text-red-600">
                    <Unlink className="h-4 w-4 mr-2" />
                    Disconnect
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Button 
              variant="outline" 
              onClick={handleConnectGoogle}
              className="gap-2"
              data-testid="connect-google-btn"
            >
              <Link2 className="h-4 w-4" />
              Connect Google
            </Button>
          )}
          
          {/* Add Event Button */}
          <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setIsDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="add-event-btn">
                <Plus className="h-4 w-4" />
                Add Event
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingEvent ? 'Edit Event' : 'Create New Event'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    data-testid="event-title-input"
                  />
                </div>

                {/* Event Type & Priority */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Event Type</Label>
                    <Select
                      value={formData.event_type}
                      onValueChange={(value) => {
                        const typeConfig = EVENT_TYPES[value];
                        setFormData({ 
                          ...formData, 
                          event_type: value,
                          priority: typeConfig?.priority || formData.priority,
                          non_movable: typeConfig?.nonMovable || false
                        });
                      }}
                    >
                      <SelectTrigger data-testid="event-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(EVENT_TYPES).filter(([k]) => k !== 'google').map(([key, config]) => {
                          const Icon = config.icon;
                          return (
                            <SelectItem key={key} value={key}>
                              <div className="flex items-center gap-2">
                                <Icon className="h-4 w-4" />
                                {config.label}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(value) => setFormData({ ...formData, priority: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="critical">🔴 Critical</SelectItem>
                        <SelectItem value="high">🟠 High</SelectItem>
                        <SelectItem value="medium">🟡 Medium</SelectItem>
                        <SelectItem value="low">🟢 Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* All Day Toggle & Non-Movable */}
                <div className="flex items-center gap-6">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="all_day"
                      checked={formData.all_day}
                      onCheckedChange={(checked) => setFormData({ ...formData, all_day: checked })}
                    />
                    <Label htmlFor="all_day" className="text-sm font-normal">All Day Event</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="non_movable"
                      checked={formData.non_movable}
                      onCheckedChange={(checked) => setFormData({ ...formData, non_movable: checked })}
                    />
                    <Label htmlFor="non_movable" className="text-sm font-normal">
                      Hard Deadline (Non-movable)
                    </Label>
                  </div>
                </div>

                {/* Date/Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{formData.all_day ? 'Date *' : 'Start Time *'}</Label>
                    <Input
                      type={formData.all_day ? 'date' : 'datetime-local'}
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                      required
                      data-testid="event-start-input"
                    />
                  </div>
                  {!formData.all_day && (
                    <div className="space-y-2">
                      <Label>End Time *</Label>
                      <Input
                        type="datetime-local"
                        value={formData.end_time}
                        onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                        required
                      />
                    </div>
                  )}
                </div>

                {/* Case & Client Linking */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Linked Case</Label>
                    <Select
                      value={formData.case_id}
                      onValueChange={(value) => {
                        const caseInfo = cases.find(c => c.case_id === value);
                        setFormData({ 
                          ...formData, 
                          case_id: value,
                          client_id: caseInfo?.client_id || formData.client_id
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select case" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {cases.map((c) => (
                          <SelectItem key={c.case_id} value={c.case_id}>
                            <div className="flex items-center gap-2">
                              <Briefcase className="h-3 w-3" />
                              {c.title || c.case_number}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Responsible Person</Label>
                    <Select
                      value={formData.owner_id}
                      onValueChange={(value) => setFormData({ ...formData, owner_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select owner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={user?.user_id || 'me'}>Me ({user?.name})</SelectItem>
                        {teamMembers.filter(m => m.user_id !== user?.user_id).map((member) => (
                          <SelectItem key={member.user_id} value={member.user_id}>
                            {member.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Location */}
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Court room, office address, or video link..."
                  />
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label>Description / Notes</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    placeholder="Additional details, preparation notes..."
                  />
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                  <Button type="submit" data-testid="save-event-btn">
                    {editingEvent ? 'Update Event' : 'Create Event'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Alerts Banner */}
      {(upcomingDeadlines.length > 0 || overdueTasks.length > 0) && (
        <div className="flex flex-wrap gap-3">
          {upcomingDeadlines.length > 0 && (
            <Card className="flex-1 min-w-[280px] bg-amber-50 border-amber-200">
              <CardContent className="p-3 flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-amber-800 text-sm">
                    {upcomingDeadlines.length} upcoming deadline{upcomingDeadlines.length > 1 ? 's' : ''} this week
                  </p>
                  <p className="text-xs text-amber-700 truncate">
                    Next: {upcomingDeadlines[0]?.title}
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="bg-white text-amber-700 border-amber-300 hover:bg-amber-100"
                  onClick={() => setViewMode('deadlines')}
                >
                  View
                </Button>
              </CardContent>
            </Card>
          )}
          
          {overdueTasks.length > 0 && (
            <Card className="flex-1 min-w-[280px] bg-red-50 border-red-200">
              <CardContent className="p-3 flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-red-800 text-sm">
                    {overdueTasks.length} overdue task{overdueTasks.length > 1 ? 's' : ''}
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="bg-white text-red-700 border-red-300 hover:bg-red-100"
                  onClick={() => navigate('/tasks?filter=overdue')}
                >
                  View Tasks
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Filters Bar */}
      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search events..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="event-search-input"
            />
          </div>
          
          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger className="w-[140px]">
              <Filter className="h-4 w-4 mr-2 text-slate-500" />
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value="critical">🔴 Critical</SelectItem>
              <SelectItem value="high">🟠 High</SelectItem>
              <SelectItem value="medium">🟡 Medium</SelectItem>
              <SelectItem value="low">🟢 Low</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="showTasks"
              checked={showTasksOnCalendar}
              onCheckedChange={setShowTasksOnCalendar}
            />
            <Label htmlFor="showTasks" className="text-sm font-normal">Show Tasks</Label>
          </div>
          
          <div className="ml-auto text-sm text-slate-500">
            {allEvents.length} event{allEvents.length !== 1 ? 's' : ''}
          </div>
        </div>
      </Card>

      {/* Main Content */}
      {viewMode === 'timeline' && selectedCase ? (
        // Case Timeline View
        <Card>
          <CardContent className="p-6">
            <CaseTimeline 
              caseId={selectedCase} 
              onItemClick={(item) => {
                if (item.type === 'event') {
                  openEventDrawer(item);
                } else if (item.type === 'task') {
                  navigate(`/tasks?highlight=${item.task_id}`);
                } else if (item.type === 'document') {
                  navigate(`/documents?highlight=${item.id}`);
                }
              }}
            />
          </CardContent>
        </Card>
      ) : viewMode === 'timeline' && !selectedCase ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Briefcase className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <h3 className="font-medium text-slate-900 mb-1">Select a Case</h3>
            <p className="text-slate-500 mb-4">Choose a case to view its timeline</p>
            <Select value={selectedCase || ''} onValueChange={setSelectedCase}>
              <SelectTrigger className="w-[300px] mx-auto">
                <SelectValue placeholder="Select case..." />
              </SelectTrigger>
              <SelectContent>
                {cases.map(c => (
                  <SelectItem key={c.case_id} value={c.case_id}>
                    {c.title || c.case_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      ) : viewMode === 'funnel' ? (
        // Master Case Funnel (Partners Only)
        <MasterCaseFunnel />
      ) : viewMode === 'rules' ? (
        // Jurisdiction Rules & Deadline Intelligence
        <Card>
          <CardContent className="p-6">
            <JurisdictionDeadlines 
              caseId={selectedCase}
              onDeadlineCalculated={(result) => {
                console.log('Deadline calculated:', result);
              }}
              onCreateEvent={(eventData) => {
                setFormData(prev => ({
                  ...prev,
                  ...eventData,
                  start_time: eventData.start_time ? new Date(eventData.start_time).toISOString().slice(0, 16) : '',
                  end_time: eventData.start_time ? new Date(new Date(eventData.start_time).getTime() + 3600000).toISOString().slice(0, 16) : '',
                }));
                setIsDialogOpen(true);
              }}
            />
          </CardContent>
        </Card>
      ) : viewMode === 'templates' ? (
        // Auto-Generated Case Calendars
        <Card>
          <CardContent className="p-6">
            <CaseCalendarTemplates 
              onGenerated={(result) => {
                fetchAllData();
                toast.success(`Generated ${result.events_created.length} events`);
              }}
            />
          </CardContent>
        </Card>
      ) : viewMode === 'reminders' ? (
        // Smart Reminders View
        <SmartReminders 
          onEventClick={(eventId) => {
            const event = events.find(e => e.event_id === eventId);
            if (event) openEventDrawer(event);
          }}
          onRefresh={fetchAllData}
        />
      ) : viewMode === 'conflicts' ? (
        // Conflict Detection View
        <ConflictDetection
          onEventClick={(eventId) => {
            const event = events.find(e => e.event_id === eventId);
            if (event) openEventDrawer(event);
          }}
          onRefresh={fetchAllData}
        />
      ) : viewMode === 'agenda' ? (
        // Agenda View
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <List className="h-5 w-5" />
              Agenda View
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[600px]">
              <div className="space-y-2">
                {allEvents.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <CalendarIcon className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                    <p>No events found</p>
                  </div>
                ) : (
                  allEvents.map((event) => {
                    const eventConfig = EVENT_TYPES[event.event_type] || EVENT_TYPES.meeting;
                    const Icon = eventConfig.icon;
                    const caseInfo = getCaseInfo(event.case_id);
                    
                    return (
                      <div
                        key={event.event_id}
                        className={cn(
                          "p-3 rounded-lg border cursor-pointer hover:shadow-sm transition-all",
                          eventConfig.color
                        )}
                        onClick={() => openEventDrawer(event)}
                      >
                        <div className="flex items-start gap-3">
                          <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium truncate">{event.title}</h4>
                              {event.non_movable && (
                                <Badge variant="outline" className="text-xs">Fixed</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-sm opacity-80">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatDate(event.start_time)}
                              </span>
                              {caseInfo && (
                                <span className="flex items-center gap-1">
                                  <Briefcase className="h-3 w-3" />
                                  {caseInfo.title}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      ) : (
        // Calendar Grid View
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5" />
                  {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
                </CardTitle>
                <div className="flex items-center gap-1">
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setCurrentMonth(new Date());
                      setSelectedDate(new Date());
                    }}
                  >
                    Today
                  </Button>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                month={currentMonth}
                onMonthChange={setCurrentMonth}
                className="rounded-md border w-full"
                components={{
                  DayContent: ({ date }) => {
                    const dateStr = date.toDateString();
                    const dateInfo = eventDates[dateStr];
                    
                    return (
                      <div className="relative w-full h-full flex items-center justify-center">
                        <span>{date.getDate()}</span>
                        {dateInfo && (
                          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex gap-0.5">
                            {dateInfo.hasCritical && (
                              <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                            )}
                            {dateInfo.hasDeadline && !dateInfo.hasCritical && (
                              <div className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                            )}
                            {dateInfo.count > 0 && !dateInfo.hasDeadline && !dateInfo.hasCritical && (
                              <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }
                }}
              />
            </CardContent>
          </Card>

          {/* Events Panel */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {formatDate(selectedDate.toISOString())}
              </CardTitle>
              <CardDescription>
                {selectedDateEvents.length} event{selectedDateEvents.length !== 1 ? 's' : ''}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-2">
                  {selectedDateEvents.length > 0 ? (
                    selectedDateEvents.map((event) => {
                      const eventConfig = EVENT_TYPES[event.event_type] || EVENT_TYPES.meeting;
                      const Icon = eventConfig.icon;
                      const caseInfo = getCaseInfo(event.case_id);
                      
                      return (
                        <div 
                          key={event.event_id}
                          className={cn(
                            "p-3 rounded-lg border cursor-pointer hover:shadow-sm transition-all",
                            eventConfig.color
                          )}
                          onClick={() => openEventDrawer(event)}
                          data-testid={`event-${event.event_id}`}
                        >
                          <div className="flex items-start justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              <h4 className="font-medium text-sm">{event.title}</h4>
                            </div>
                            {event.is_task && (
                              <Badge variant="outline" className="text-xs">Task</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs opacity-80">
                            <Clock className="h-3 w-3" />
                            <span>
                              {new Date(event.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {caseInfo && (
                              <>
                                <span>•</span>
                                <Briefcase className="h-3 w-3" />
                                <span className="truncate max-w-[100px]">{caseInfo.title}</span>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <CalendarIcon className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                      <p className="text-sm">No events scheduled</p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-3"
                        onClick={() => setIsDialogOpen(true)}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Event
                      </Button>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Event Detail Drawer */}
      <Sheet open={eventDrawerOpen} onOpenChange={setEventDrawerOpen}>
        <SheetContent className="sm:max-w-[480px]">
          {selectedEvent && (
            <>
              <SheetHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <SheetTitle className="text-xl">{selectedEvent.title}</SheetTitle>
                    <SheetDescription>
                      {EVENT_TYPES[selectedEvent.event_type]?.label || 'Event'}
                    </SheetDescription>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {selectedEvent.source !== 'google' && !selectedEvent.is_task && (
                        <DropdownMenuItem onClick={() => openEditEvent(selectedEvent)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Event
                        </DropdownMenuItem>
                      )}
                      {(selectedEvent.event_type === 'client_meeting' || selectedEvent.event_type === 'court_appearance') && (
                        <DropdownMenuItem onClick={() => handleStartTimer(selectedEvent)}>
                          <Play className="h-4 w-4 mr-2" />
                          Start Timer
                        </DropdownMenuItem>
                      )}
                      {selectedEvent.source !== 'google' && !selectedEvent.is_task && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleDeleteEvent(selectedEvent.event_id)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </SheetHeader>
              
              <div className="mt-6 space-y-6">
                {/* Time */}
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="font-medium">
                      {formatDateTime(selectedEvent.start_time)}
                    </p>
                    {selectedEvent.end_time && (
                      <p className="text-sm text-slate-500">
                        to {formatDateTime(selectedEvent.end_time)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Location */}
                {selectedEvent.location && (
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-slate-400 mt-0.5" />
                    <p>{selectedEvent.location}</p>
                  </div>
                )}

                {/* Linked Case */}
                {selectedEvent.case_id && (
                  <div className="flex items-start gap-3">
                    <Briefcase className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="font-medium">Linked Case</p>
                      {(() => {
                        const caseInfo = getCaseInfo(selectedEvent.case_id);
                        return caseInfo ? (
                          <div className="mt-1">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => navigate(`/cases/${selectedEvent.case_id}`)}
                              className="gap-2"
                            >
                              {caseInfo.title || caseInfo.case_number}
                              <ExternalLink className="h-3 w-3" />
                            </Button>
                            <div className="flex gap-2 mt-2">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => navigate(`/cases/${selectedEvent.case_id}?tab=documents`)}
                              >
                                <FileText className="h-4 w-4 mr-1" />
                                Documents
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500">Case not found</p>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Owner */}
                {selectedEvent.owner_id && (
                  <div className="flex items-start gap-3">
                    <User className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="font-medium">Responsible</p>
                      <p className="text-sm text-slate-600">
                        {teamMembers.find(m => m.user_id === selectedEvent.owner_id)?.name || 
                         (selectedEvent.owner_id === user?.user_id ? user?.name : 'Unknown')}
                      </p>
                    </div>
                  </div>
                )}

                {/* Description */}
                {selectedEvent.description && (
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="font-medium">Notes</p>
                      <p className="text-sm text-slate-600 whitespace-pre-wrap mt-1">
                        {selectedEvent.description}
                      </p>
                    </div>
                  </div>
                )}

                {/* Priority Badge */}
                <div className="pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">Priority:</span>
                    <Badge variant={selectedEvent.priority === 'critical' ? 'destructive' : 'outline'}>
                      {selectedEvent.priority?.charAt(0).toUpperCase() + selectedEvent.priority?.slice(1) || 'Medium'}
                    </Badge>
                    {selectedEvent.non_movable && (
                      <Badge variant="outline">Hard Deadline</Badge>
                    )}
                  </div>
                </div>

                {/* Time Tracking Buttons */}
                <div className="pt-4 border-t">
                  {activeTimer && activeTimer.eventId === selectedEvent.event_id ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50 border border-green-200">
                        <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                        <span className="text-sm text-green-700">Timer running...</span>
                      </div>
                      <Button 
                        className="w-full gap-2" 
                        variant="destructive"
                        onClick={() => handleStopTimer(selectedEvent)}
                        disabled={timerLoading}
                      >
                        {timerLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Square className="h-4 w-4" />
                        )}
                        Stop Timer
                      </Button>
                    </div>
                  ) : (
                    <Button 
                      className="w-full gap-2" 
                      onClick={() => handleStartTimer(selectedEvent)}
                      disabled={timerLoading || (activeTimer && activeTimer.eventId !== selectedEvent.event_id)}
                    >
                      {timerLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Timer className="h-4 w-4" />
                      )}
                      Start Time Tracking
                    </Button>
                  )}
                  {activeTimer && activeTimer.eventId !== selectedEvent.event_id && (
                    <p className="text-xs text-amber-600 mt-2">
                      Timer already running for "{activeTimer.eventTitle}"
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default CalendarPage;
